/**
 * SourceAttributionBadge - Displays source attribution for extracted data
 *
 * Shows which documents contributed to an extracted value, with confidence
 * levels and authority status. Used with Census-First pipeline results.
 */

import { useState } from "react";
import {
  FileText,
  ShieldCheck,
  AlertCircle,
  HelpCircle,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

export type AuthorityLevel =
  | "definitive"
  | "validated"
  | "inferred"
  | "unvalidated";

export interface SourceInfo {
  fileName: string;
  confidence: number;
  authority?: AuthorityLevel;
  extractedAt?: string;
  page?: number;
  line?: number;
}

interface SourceAttributionBadgeProps {
  /** List of sources that contributed to this value */
  sources: SourceInfo[];
  /** Overall confidence for the value */
  confidence?: number;
  /** Whether to show a compact or expanded view */
  variant?: "compact" | "expanded";
  /** Additional class names */
  className?: string;
}

/**
 * Get authority level icon and color
 */
function getAuthorityInfo(authority?: AuthorityLevel): {
  icon: typeof ShieldCheck;
  color: string;
  label: string;
} {
  switch (authority) {
    case "definitive":
      return {
        icon: ShieldCheck,
        color: "text-[var(--success)]",
        label: "Definitive Source",
      };
    case "validated":
      return {
        icon: ShieldCheck,
        color: "text-primary",
        label: "Validated",
      };
    case "inferred":
      return {
        icon: HelpCircle,
        color: "text-[var(--warning)]",
        label: "Inferred",
      };
    default:
      return {
        icon: AlertCircle,
        color: "text-muted-foreground",
        label: "Unvalidated",
      };
  }
}

/**
 * Get confidence level color
 */
function getConfidenceColor(confidence: number): string {
  if (confidence >= 90) return "bg-[var(--success)]/20 text-[var(--success)]";
  if (confidence >= 70) return "bg-[var(--warning)]/20 text-[var(--warning)]";
  if (confidence >= 50) return "bg-[var(--info)]/20 text-[var(--info)]";
  return "bg-muted text-muted-foreground";
}

export function SourceAttributionBadge({
  sources,
  confidence,
  variant = "compact",
  className,
}: SourceAttributionBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);

  // No sources to show
  if (!sources || sources.length === 0) {
    return null;
  }

  // Get the primary source (highest confidence)
  const primarySource = sources.reduce((best, current) =>
    current.confidence > best.confidence ? current : best
  );

  const {
    icon: AuthorityIcon,
    color: authorityColor,
    label: authorityLabel,
  } = getAuthorityInfo(primarySource.authority);

  // Compact variant: just show an icon with tooltip
  if (variant === "compact") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={cn(
                "inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors",
                className
              )}
            >
              <AuthorityIcon className={cn("h-3.5 w-3.5", authorityColor)} />
              {sources.length > 1 && (
                <span className="text-[10px]">+{sources.length - 1}</span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-2">
              <div className="font-medium text-xs">{authorityLabel}</div>
              <div className="text-xs text-muted-foreground">
                Source: {primarySource.fileName}
              </div>
              {primarySource.confidence && (
                <div className="text-xs">
                  Confidence: {primarySource.confidence}%
                </div>
              )}
              {sources.length > 1 && (
                <div className="text-xs text-muted-foreground">
                  +{sources.length - 1} additional source
                  {sources.length > 2 ? "s" : ""}
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded variant: show popover with full details
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs",
            "bg-muted/50 hover:bg-muted transition-colors",
            className
          )}
        >
          <AuthorityIcon className={cn("h-3.5 w-3.5", authorityColor)} />
          <span className="text-muted-foreground">
            {sources.length} source{sources.length !== 1 ? "s" : ""}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b">
          <h4 className="font-medium text-sm">Source Attribution</h4>
          {confidence !== undefined && (
            <Badge
              variant="secondary"
              className={cn("mt-1 text-xs", getConfidenceColor(confidence))}
            >
              {confidence}% confidence
            </Badge>
          )}
        </div>
        <div className="max-h-60 overflow-y-auto">
          {sources.map((source, index) => {
            const {
              icon: SrcIcon,
              color: srcColor,
              label: srcLabel,
            } = getAuthorityInfo(source.authority);

            return (
              <div
                key={index}
                className={cn(
                  "px-3 py-2 flex items-start gap-2",
                  index !== sources.length - 1 && "border-b"
                )}
              >
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">
                      {source.fileName}
                    </span>
                    <SrcIcon className={cn("h-3.5 w-3.5 shrink-0", srcColor)} />
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground">
                      {srcLabel}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {source.confidence}%
                    </span>
                  </div>
                  {(source.page || source.line) && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {source.page && `Page ${source.page}`}
                      {source.page && source.line && ", "}
                      {source.line && `Line ${source.line}`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * SourceBadgeInline - A minimal inline badge showing source count
 * Used in table rows and compact layouts
 */
export function SourceBadgeInline({
  sources,
  className,
}: {
  sources?: string[];
  className?: string;
}) {
  if (!sources || sources.length === 0) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 text-xs text-muted-foreground",
              className
            )}
          >
            <FileText className="h-3 w-3" />
            {sources.length}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="text-xs">
            <div className="font-medium mb-1">Sources:</div>
            <ul className="list-disc list-inside space-y-0.5">
              {sources.slice(0, 5).map((src, i) => (
                <li key={i} className="truncate max-w-[200px]">
                  {src}
                </li>
              ))}
              {sources.length > 5 && (
                <li className="text-muted-foreground">
                  +{sources.length - 5} more
                </li>
              )}
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
